import { FormEvent, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { Button, Checkbox, Container, FormControlLabel, Grid, Link, TextField } from '@mui/material'
import { CognitoChallengeNameSchema, LoginAccount200Response, ResponseError } from '../../clients'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlertWithTimeout } from '../../redux/features/alerts/alertsHelpers'
import { useAppDispatch } from '../../redux/hooks'
import { getAuthService } from '../../services/authService'
import { authenticate } from '../../redux/features/auth/authSlice'
import { RecaptchaService } from '../../services/recaptchaService'

enum LoginStep {
    Default,
    MFA
}

export const SignIn = () => {

    const dispatch = useAppDispatch()
    const [email, setEmail] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [passwordError, setPasswordError] = useState<string>('')
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [step, setStep] = useState<LoginStep>(LoginStep.Default)
    const [mfaCode, setMfaCode] = useState<string>('')
    const [mfaError, setMfaError] = useState<string>('')
    const [mfaDestination, setMfaDestination] = useState<string>('')
    const [rememberMe, setRememberMe] = useState<boolean>(false)

    const cognitoSub = useRef<string>('')
    const cognitoSession = useRef<string>('')
    const mfaChallengeType = useRef<CognitoChallengeNameSchema | null>(null)

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const recaptchaService = new RecaptchaService()
        const recaptchaToken = await recaptchaService.getToken()
            .catch(() => '')
        if (handleEmailBlur() || submitting) {
            return
        }
        setSubmitting(true)
        try {
            const authService = await getAuthService()
            const res = await authService.passwordLogin(email, password, rememberMe, recaptchaToken)
            if (res.challengeName === 'SMS_MFA') {
                setStep(LoginStep.MFA)
                setMfaDestination(res.challengeParameters?.codeDeliveryDestination ?? '')
                mfaChallengeType.current = res.challengeName
                cognitoSub.current = res.username ?? ''
                cognitoSession.current = res.session ?? ''
            }
            if (res.authenticationResult) {
                dispatch(authenticate(res.authenticationResult))
            }
        } catch (err) {
            const error = err as ResponseError
            const res = error.response
            const text = await res.text().catch(() => null)
            if (text === 'Incorrect username/password') {
                setPasswordError('Incorrect password')
                return setSubmitting(false)
            }
            const alert = new AppAlert('error', 'Something went wrong, try again later')
            addAlertWithTimeout(dispatch, alert)
        }
        setSubmitting(false)
    }

    const handleMFASubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!mfaCode || !mfaChallengeType.current || submitting) {
            return
        }
        setMfaError('')
        try {
            const authService = await getAuthService()
            let res: LoginAccount200Response | null = null
            if (mfaChallengeType.current === 'SMS_MFA') {
                res = await authService.respondToSMSToken(cognitoSub.current, mfaCode, cognitoSession.current)
            } else if (mfaChallengeType.current === 'SOFTWARE_TOKEN_MFA') {
                res = await authService.respondToSoftwareToken(cognitoSub.current, mfaCode, cognitoSession.current)
            }
            if (res?.authenticationResult) {
                dispatch(authenticate(res.authenticationResult))
            }
        } catch (err) {
            const error = err as ResponseError
            const res = error.response
            const text = await res.text().catch(() => null)
            if (text === 'Invalid session') {
                setStep(LoginStep.Default)
                const alert = new AppAlert('error', 'Session expired. Please try again.')
                addAlertWithTimeout(dispatch, alert)
            }
            if (text === 'Invalid code') {
                setMfaError('Invalid Code')
            }
        }
    }

    const handleEmailFocus = () => {
        setEmailError('')
    }

    const handlePasswordFocus = () => {
        setPasswordError('')
    }

    const handleMfaFocus = () => {
        setMfaError('')
    }

    const handleEmailBlur = (): string => {
        const emailRegex = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g)
        if (!email.match(emailRegex)?.length) {
            const err = 'Invalid email address'
            setEmailError(err)
            return err
        }
        return ''
    }

    const handleGoogleSignIn = () => {
        const idpIdentifierUrl = `https://${import.meta.env.VITE_COGNITO_AUTH_DOMAIN}/oauth2/authorize?response_type=code&client_id=${import.meta.env.VITE_COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth/login-callback`)}&identity_provider=Google`
        window.location.href = idpIdentifierUrl
    }

    // const handleFacebookSignIn = () => {
    //   const idpIdentifierUrl = `${import.meta.env.VITE_COGNITO_AUTH_DOMAIN}/oauth2/authorize?response_type=code&client_id=${import.meta.env.VITE_COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth/login-callback`)}&identity_provider=Facebook`
    //   window.location.href = idpIdentifierUrl
    // }

    if (step === LoginStep.MFA) {
        return (
            <Container component='div' maxWidth='sm'>
                <Typography component="h1" variant="h5" sx={{ mt: 8, textAlign: 'center' }}>
                    Sign In
                </Typography>
                <Box component='form' onSubmit={handleMFASubmit} sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <Typography component='h2' variant='subtitle1' sx={{ mt: 2, textAlign: 'center' }}>Multi-Factor Authentication</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography component='p' variant='body1' sx={{ mt: 2, textAlign: 'center' }}>Enter the code sent to {mfaDestination}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                required
                                fullWidth
                                value={mfaCode}
                                error={!!mfaError}
                                helperText={mfaError}
                                onFocus={handleMfaFocus}
                                onChange={e => setMfaCode(e.target.value)}
                                label='Code'
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Button disabled={submitting || !mfaCode} type='submit' fullWidth variant="contained" color="primary" sx={{ mt: 2, mb: 2 }}>Submit</Button>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        )
    }

    return (
        <Container component='div' maxWidth='sm'>
            <Typography component="h1" variant="h5" sx={{ mt: 8, textAlign: 'center' }}>
                Sign In
            </Typography>
            <Box component='form' onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            fullWidth
                            error={!!emailError}
                            helperText={emailError}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={handleEmailFocus}
                            onBlur={handleEmailBlur}
                            label='Email'
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            fullWidth
                            value={password}
                            error={!!passwordError}
                            helperText={passwordError}
                            onFocus={handlePasswordFocus}
                            onChange={(e) => setPassword(e.target.value)}
                            type='password'
                            label='Password'
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: 1, display: 'flex', justifyContent: 'end' }}>
                    <FormControlLabel control={<Checkbox value={rememberMe} onChange={e => setRememberMe(e.target.checked)} />} label='Remember me' />
                </Grid>
                <Grid container sx={{ mt: 1 }}>
                    <Grid size={{ xs: 6 }}>
                        <RouterLink to='/auth/forgot-password'><Link component='p'>Forgot password?</Link></RouterLink>
                    </Grid>
                    <Grid size={{ xs: 6 }} display='flex' justifyContent='end'>
                        <RouterLink to='/auth/create-account'><Link component='p'>Don't have an account? Sign up</Link></RouterLink>
                    </Grid>
                </Grid>
                <Box sx={{ textAlign: 'center', my: 1, mb: 0 }}>
                    <Typography variant='caption'>
                        This site is protected by reCAPTCHA and the Google&nbsp;
                        <a href="https://policies.google.com/privacy">Privacy Policy</a> and&nbsp;
                        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                    </Typography>
                </Box>
                <Button disabled={submitting || !email || !password || !!emailError} type='submit' fullWidth variant="contained" color="primary" sx={{ mt: 2, mb: 2 }}>Sign In</Button>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Button variant='contained' fullWidth color='secondary' onClick={handleGoogleSignIn}>
                            <Box sx={{ mr: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 24 24">
                                    <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"></path>
                                </svg>
                            </Box>
                            Sign in with Google
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    )
}